import { useMobileViewerUi } from "@/components/common/mobile-viewer-ui-context";
import { useAppSelector } from "@/store/store-hooks";
import {
  FaroIconButton,
  HeaderBar,
  UserMenu,
  HamburgerMenuIcon,
  ThreeDotsIcon,
  UserAvatarIcon,
} from "@faro-lotv/flat-ui";
import { selectProjectName } from "@faro-lotv/project-source";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useState, type ComponentProps, type JSX } from "react";
import { MobileViewerSheet } from "./mobile-viewer-sheet";
import { SavingIndicator } from "./saving-indicator";

/** @returns The compact viewer header, sharing desktop action and authentication handlers. */
export function MobileViewerHeader({
  children,
  content,
  drawerContent,
  userDisplayInfo,
  links,
  appVersion,
  onLogInClick,
  onLogOutClick,
  onAccountAndSecurityClick,
  userMenuContent,
}: ComponentProps<typeof HeaderBar>): JSX.Element {
  const mobileUi = useMobileViewerUi();
  const projectName = useAppSelector(selectProjectName);
  const [accountAnchor, setAccountAnchor] = useState<HTMLButtonElement | null>(
    null,
  );
  return (
    <>
      <Stack
        component="header"
        aria-label="Phone header"
        direction="row"
        sx={{
          alignItems: "center",
          minHeight: "calc(48px + env(safe-area-inset-top, 0px))",
          flexShrink: 0,
          pt: "env(safe-area-inset-top, 0px)",
          px: 0.5,
          gap: 0.5,
          bgcolor: "#f6f7f8",
          color: "#303845",
          "& button": { minWidth: 44, minHeight: 44 },
        }}
      >
        <Tooltip title="Project">
          <FaroIconButton
            aria-label="Open project navigation"
            aria-haspopup="dialog"
            aria-expanded={mobileUi?.surface === "project"}
            onClick={() => mobileUi?.setSurface("project")}
          >
            <HamburgerMenuIcon />
          </FaroIconButton>
        </Tooltip>
        <Typography
          noWrap
          title={projectName}
          sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}
        >
          {projectName}
        </Typography>
        <SavingIndicator />
        <Tooltip title="More">
          <FaroIconButton
            aria-label="More viewer actions"
            aria-haspopup="dialog"
            aria-expanded={mobileUi?.surface === "more"}
            onClick={() => mobileUi?.setSurface("more")}
          >
            <ThreeDotsIcon sx={{ transform: "rotate(90deg)" }} />
          </FaroIconButton>
        </Tooltip>
      </Stack>
      <MobileViewerSheet name="project" title="Project">
        <Box
          sx={{
            bgcolor: "#fff",
            color: "#303845",
            p: 1,
            flex: 1,
            minHeight: 600,
            display: "flex",
            flexDirection: "column",
            "& button": { minHeight: 44 },
          }}
        >
          {content}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
            {drawerContent}
          </Box>
        </Box>
      </MobileViewerSheet>
      <MobileViewerSheet name="more" title="More">
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            bgcolor: "#f6f7f8",
            color: "#303845",
            p: 1,
            borderRadius: 1,
          }}
        >
          {children}
          <Tooltip title="Account">
            <FaroIconButton
              aria-label="open user menu"
              onClick={(event) => setAccountAnchor(event.currentTarget)}
            >
              <UserAvatarIcon />
            </FaroIconButton>
          </Tooltip>
        </Stack>
      </MobileViewerSheet>
      <UserMenu
        anchorElement={accountAnchor ?? undefined}
        open={!!accountAnchor && mobileUi?.surface === "more"}
        closeMenu={() => setAccountAnchor(null)}
        userDisplayInfo={userDisplayInfo}
        appVersion={appVersion}
        links={[
          { href: links?.termsOfServiceUrl, text: "Terms of service" },
          { href: links?.privacyPolicyUrl, text: "Privacy policy" },
          { href: links?.imprintUrl, text: "Imprint" },
        ]}
        onLoginClick={onLogInClick}
        onLogoutClick={onLogOutClick}
        onSettingsClick={onAccountAndSecurityClick}
      >
        {userMenuContent}
      </UserMenu>
    </>
  );
}
